import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { AppConfirmService } from 'src/app/shared/services/app-confirm/app-confirm.service';
import { ErrorHandlerService } from 'src/app/shared/services/error-handler.service';
import { CourseCreatorService } from 'src/app/views/displayer/course-creator/course-creator.service';
import { PathListComponent } from 'src/app/views/displayer/path/path-list/path-list.component';
import { PathService } from 'src/app/views/displayer/path/path.service';
import { MySpaceService } from 'src/app/views/my-space/my-space.service';
import { MaterialModule } from '../../material.module';
import { AddSectionComponent } from '../../services/add-section/add-section.component';
import { ToastService } from '../../services/toast/toast.service';
import { ShareMenuDialogComponent } from '../share-menu-dialog/share-menu-dialog.component';
import { CourseCardData } from './course-card.dto';

@Component({
  selector: 'app-course-card',
  templateUrl: './course-card.component.html',
  standalone: true,
  imports: [MaterialModule, TranslateModule],
})
export class CourseCardComponent implements OnInit, OnDestroy {
  @Input() course: CourseCardData;
  @Input() searchTerm;
  @Input() isArchived = false;
  createMode;
  createModeSubscription;
  @Output() shouldReload = new EventEmitter<string>();
  actualName;
  actualDescription;
  translateSub: Subscription;

  constructor(
    private router: Router,
    private appService: AppService,
    private confirmService: AppConfirmService,
    private courseCreatorService: CourseCreatorService,
    private toast: ToastService,
    private errorHandler: ErrorHandlerService,
    private dialog: MatDialog,
    private mySpaceService: MySpaceService,
    private translate: TranslateService,
    private breakpointObserver: BreakpointObserver,
    private bottomSheet: MatBottomSheet,
    private pathService: PathService
  ) {
    this.translateSub = this.translate.onLangChange.subscribe(() => {});
  }

  ngOnInit() {
    this.createModeSubscription = this.appService.createMode$.subscribe((createMode) => (this.createMode = createMode));
  }

  ngOnDestroy() {
    this.translateSub.unsubscribe();
    this.createModeSubscription.unsubscribe();
  }

  archiveCourse(courseID) {
    this.confirmService.confirm({ message: this.translate.instant('Confirm.ArchiveCourse') }).subscribe((res) => {
      if (res) {
        this.mySpaceService.addToArchive({ courseID }).subscribe(() => {
          this.shouldReload.emit();
        });
      }
    });
  }

  unarchiveCourse(courseID) {
    this.confirmService.confirm({ message: this.translate.instant('Confirm.UnarchiveCourse') }).subscribe((res) => {
      if (res) {
        this.mySpaceService.removeFromArchive({ courseID }).subscribe(() => {
          this.shouldReload.emit();
        });
      }
    });
  }

  openShareMenu(courseID) {
    const isSmallScreen = this.breakpointObserver.isMatched('(max-width: 599px)');

    if (isSmallScreen) {
      this.bottomSheet.open(ShareMenuDialogComponent, {
        data: {
          courseID: courseID,
        },
      });
    } else {
      this.dialog.open(ShareMenuDialogComponent, {
        width: '600px',
        maxHeight: '80vh',
        data: {
          courseID: courseID,
        },
      });
    }
  }

  openAddDialog(dialogType, courseID = null, sectionID = null, lectureID = null, lectureName = null) {
    this.dialog
      .open(AddSectionComponent, {
        data: {
          type: dialogType,
          courseID,
          sectionID,
          lectureID,
          lectureName,
        },
        width: '500px',
      })
      .afterClosed()
      .subscribe((res) => {
        if (res === 'ok') {
          this.shouldReload.emit();
        }
      });
  }

  navigate(): void {
    this.router.navigateByUrl(`/knowledge/overview/${this.course._id}`);
  }

  deleteCourse(courseID, courseName) {
    this.translate.get('Global.Delete').subscribe((delString) => {
      this.confirmService.confirm({ message: `${delString} ${courseName}?` }).subscribe((res) => {
        if (res) {
          this.courseCreatorService.deleteCourse(courseID).subscribe(
            () => {
              this.toast.snackbar.open(this.translate.instant('Snackbar.CourseDeletedSuccess'), 'OK', { duration: 4000 });
              this.shouldReload.emit();
            },
            (err) => {
              if (!this.errorHandler.handle401Error(err)) {
                this.toast.snackbar.open(this.translate.instant('Snackbar.CourseDeletedFail'), 'Close', {
                  duration: 2000,
                });
              }
            }
          );
        }
      });
    });
  }

  addCourseToPath(courseID) {
    this.dialog
      .open(PathListComponent, {
        data: { id: courseID },
        width: '80%',
        height: '70%',
      })
      .afterClosed()
      .subscribe((path) => {
        this.pathService.addCourseToPath({ courseID, pathID: path._id }).subscribe({
          next: () => {
            this.toast.snackbar.open(this.translate.instant('Snackbar.Done'), 'Ok', {
              duration: 2000,
            });
          },
          error: (err) => {
            console.error(err);
          },
        });
      });
  }
}
